import Component from '@ux/component';
import NavigationBottom from './nav-bottom';
import NavigationTop from './nav-top';
import React from 'react';
import { breakpoints } from './enums';
import PropTypes from 'prop-types';
import { URL } from '@ux/util';
import memoize from 'memoize-one';

const isDataAttr = /^data-[a-zA-Z0-9-]+$/;

class Header extends Component {
  constructor() {
    super(...arguments);
    this.currentHref = this.getCurrentHref();
    this.setDataAttrs = this.setDataAttrs.bind(this);
    this.setDataAttrsOnNav = memoize(navigation => {
      navigation.map(this.setDataAttrs);
    });
  }

  /**
   * Add eventListeners for breakpoints.
   *
   * @api private
   */
  componentDidMount() {
    this.breakpoint(...breakpoints);
  }

  /**
   * Remove the eventListeners of breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  /**
   * Sets a dataAttrs field containing all the `data-` attributes on a given item.
   * @param  {object} item A navigation item
   * @returns {Object} The updated navigation item with a `dataAttrs` field
   * @private
   */
  setDataAttrs(item) {
    if (!item) return;

    item.dataAttrs = Object.keys(item).reduce(function (acc, k) {
      if (isDataAttr.test(k)) {
        acc[k] = item[k];
      }
      return acc;
    }, {});

    if (item.children) {
      item.children.forEach(this.setDataAttrs);
    }

    return item;
  }

  /**
   * Get the current page href for comparison of active item
   *
   * @returns {String} current href
   * @private
   */
  getCurrentHref() {
    //
    // Logic originally from
    // https://github.secureserver.net/UX/product-nav/blob/119ebd4ffc55e41a4bfebe1c973f55de0c3e35bc/lib/product-nav.js#L37
    //
    let { currentHref } = this.props;
    if (this.isBrowser) {
      currentHref = document.location.href;
    }

    if (currentHref) {
      const currentUrl = new URL(currentHref, true);
      const qsCurrentHref = currentUrl.query.currentPage;
      if (qsCurrentHref) {
        currentHref = qsCurrentHref;
      } else {
        // Remove query string for comparisons
        currentUrl.set('query', {});
        currentHref = currentUrl.href;
      }
    }

    return currentHref;
  }

  render() {
    const props = this.props;
    const { breakpoint } = this.state;
    const { mobileSubNav, loggedIn, customWaffleLinks } = props;
    const isDesktop = !~['mobile', 'phablet', 'tablet'].indexOf(breakpoint);
    const isReseller = props.privateLabelId !== 1;
    const showMobileSubNav =
      mobileSubNav === 'B' && !isReseller && this.isMobile;
    const showHamburger =
      isReseller ||
      this.state.breakpoint === 'tablet' ||
      (mobileSubNav && mobileSubNav !== 'B') || // NOTE: mobileSubNav experiment does not apply to all visitors - just shoppers - so
      // we must explicitly check for loggedIn = false and render the hamburger
      // since we wont be receiving a value for mobileSubNav
      loggedIn === false;

    // For internal-header only for custom links
    const showCustomWaffleMenu = props.preset === 'internal-header' && customWaffleLinks && (customWaffleLinks.topLinks.length || customWaffleLinks.quickLinks.length);

    let showWaffleMenu = false;
    if (props.preset === 'application-header' && !isReseller) {
      if (['help', 'community', 'system-status'].indexOf(props.app) === -1) {
        // the help app is the only app with a logged-out state for app-header
        // if the app is not help, we dont have to check for loggedin and
        // can render server side to avoid flashing
        showWaffleMenu = true;
      } else if (loggedIn) {
        showWaffleMenu = true;
      }
    } else if (showCustomWaffleMenu) {
      showWaffleMenu = true;
    }

    const classNames = this.classNames('manifest', { pl: isReseller });

    this.setDataAttrsOnNav(props.navigation);

    return (
      <header className={ classNames }>
        <NavigationTop
          { ...props }
          CartComponent={ props.CartComponent }
          showWaffleMenu={ showWaffleMenu }
          customWaffleLinks={ showCustomWaffleMenu ? customWaffleLinks : null }
          isMobile={ this.isMobile }
          isTablet={ this.state.breakpoint === 'tablet' }
          showHamburger={ showHamburger }
          mobileSubNav={ showMobileSubNav }
          showLogoTray={ showMobileSubNav }
          currentHref={ this.currentHref }
        />
        {(showMobileSubNav || isDesktop) && (
          <NavigationBottom
            { ...props }
            renderMobile={ !showMobileSubNav }
            mobileSubNav={ showMobileSubNav }
            currentHref={ this.currentHref }
          />
        )}
      </header>
    );
  }
}

Header.propTypes = {
  app: PropTypes.string.isRequired,
  currentHref: PropTypes.string,
  loggedIn: PropTypes.bool,
  mobileSubNav: PropTypes.string,
  navigation: PropTypes.array.isRequired,
  CartComponent: PropTypes.elementType,
  features: PropTypes.object,
  preset: PropTypes.string,
  privateLabelId: PropTypes.number,
  showWaffleMenu: PropTypes.bool,
  customWaffleLinks: PropTypes.object
};

export default Header;
